export const INDIA_CURRENCY_DEFAULTS = Object.freeze({
  country_code: "IN",
  currency_code: "INR",
  locale: "en-IN",
  timezone: "Asia/Kolkata",
});

const INR_DISPLAY_RATES = Object.freeze({
  INR: 1,
  USD: 0.012,
  GBP: 0.0094,
  AED: 0.044,
  SGD: 0.016,
  AUD: 0.019,
  CAD: 0.016,
  NZD: 0.02,
});

const PRINTER_CURRENCY_FALLBACKS = Object.freeze({
  INR: "Rs.",
  GBP: "GBP",
  EUR: "EUR",
  JPY: "JPY",
});

const UI_CURRENCY_SYMBOL_OVERRIDES = Object.freeze({
  NPR: "रु",
});

const AMBIGUOUS_RUPEE_SYMBOL_RE = /^(?:₹|Rs\.?)$/i;

const unwrap = (value) => {
  if (!value || typeof value !== "object") return {};
  return value.data && typeof value.data === "object"
    ? { ...value, ...value.data }
    : value;
};

const firstText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const validCurrency = (value) => {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : INDIA_CURRENCY_DEFAULTS.currency_code;
};

const validLocale = (value) => {
  const locale = String(value || "").trim().replace("_", "-");
  if (!locale) return INDIA_CURRENCY_DEFAULTS.locale;
  try {
    new Intl.NumberFormat(locale).format(0);
    return locale;
  } catch (_error) {
    return INDIA_CURRENCY_DEFAULTS.locale;
  }
};

const finiteNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const fraction = (value, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.floor(number) : fallback;
};

export const resolveCurrencyContext = (primary = {}, fallback = {}) => {
  const source = unwrap(primary);
  const backup = unwrap(fallback);
  return {
    countryCode: firstText(
      source.country_code,
      source.countryCode,
      backup.country_code,
      backup.countryCode,
      INDIA_CURRENCY_DEFAULTS.country_code
    ).toUpperCase(),
    currencyCode: validCurrency(
      firstText(source.currency_code, source.currencyCode, backup.currency_code, backup.currencyCode)
    ),
    locale: validLocale(firstText(source.locale, backup.locale, INDIA_CURRENCY_DEFAULTS.locale)),
  };
};

export const readStoredCurrencyContext = () => {
  if (typeof window === "undefined") return resolveCurrencyContext();
  try {
    return resolveCurrencyContext(JSON.parse(window.localStorage.getItem("userData") || "{}"));
  } catch (_error) {
    return resolveCurrencyContext();
  }
};

export const getCurrencySymbol = (context) => {
  const settings = resolveCurrencyContext(context);
  if (settings.currencyCode === "INR") return "₹";
  if (UI_CURRENCY_SYMBOL_OVERRIDES[settings.currencyCode]) {
    return UI_CURRENCY_SYMBOL_OVERRIDES[settings.currencyCode];
  }
  try {
    const part = new Intl.NumberFormat(settings.locale, {
      style: "currency",
      currency: settings.currencyCode,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0,
    }).formatToParts(0).find((entry) => entry.type === "currency");
    const symbol = part?.value || settings.currencyCode;
    if (settings.currencyCode !== "INR" && AMBIGUOUS_RUPEE_SYMBOL_RE.test(symbol.trim())) {
      return settings.currencyCode;
    }
    return symbol;
  } catch (_error) {
    return settings.currencyCode;
  }
};

export const getPrinterSafeCurrencySymbol = (context) => {
  const settings = resolveCurrencyContext(context);
  const symbol = getCurrencySymbol(settings).trim();
  if (/^[\x20-\x7E]+$/.test(symbol)) return symbol;
  return PRINTER_CURRENCY_FALLBACKS[settings.currencyCode] || settings.currencyCode;
};

export const convertInrAmount = (value, context) =>
  finiteNumber(value) * (INR_DISPLAY_RATES[resolveCurrencyContext(context).currencyCode] || 1);

export const formatMoney = (value, context, options = {}) => {
  const settings = resolveCurrencyContext(context);
  const minimumFractionDigits = fraction(options.minimumFractionDigits, options.fractionDigits ?? 2);
  const maximumFractionDigits = Math.max(
    minimumFractionDigits,
    fraction(options.maximumFractionDigits, options.fractionDigits ?? 2)
  );
  try {
    const formatterOptions = {
      style: "currency",
      currency: settings.currencyCode,
      currencyDisplay: options.currencyDisplay || "narrowSymbol",
      minimumFractionDigits,
      maximumFractionDigits,
    };
    let formatted = new Intl.NumberFormat(settings.locale, formatterOptions).format(finiteNumber(value));
    if (settings.currencyCode !== "INR") {
      const currencyPart = new Intl.NumberFormat(settings.locale, formatterOptions)
        .formatToParts(finiteNumber(value))
        .find((part) => part.type === "currency")?.value;
      const uiOverride = UI_CURRENCY_SYMBOL_OVERRIDES[settings.currencyCode];
      if (currencyPart && uiOverride) {
        formatted = formatted.replace(currencyPart, uiOverride);
      } else if (currencyPart && AMBIGUOUS_RUPEE_SYMBOL_RE.test(currencyPart.trim())) {
        formatted = formatted.replace(
          currencyPart,
          settings.currencyCode
        );
      }
    }
    return settings.currencyCode === "INR" ? formatted.replace(/₹\s?/, "₹ ") : formatted;
  } catch (_error) {
    return `${getCurrencySymbol(settings)} ${finiteNumber(value).toFixed(maximumFractionDigits)}`;
  }
};

export const formatStoredMoney = (value, options = {}) =>
  formatMoney(value, readStoredCurrencyContext(), options);

export const formatNumberWithLocale = (value, context, options = {}) => {
  const settings = resolveCurrencyContext(context);
  const minimumFractionDigits = fraction(options.minimumFractionDigits, options.fractionDigits ?? 0);
  const maximumFractionDigits = Math.max(
    minimumFractionDigits,
    fraction(options.maximumFractionDigits, options.fractionDigits ?? minimumFractionDigits)
  );
  try {
    return new Intl.NumberFormat(settings.locale, {
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(finiteNumber(value));
  } catch (_error) {
    return finiteNumber(value).toFixed(maximumFractionDigits);
  }
};
